import { type CSSProperties, useEffect, useRef, useState } from 'react'
import { isFontLoaded, loadFont } from '../../../../lib/font-loader'
import { useGoogleFontCatalogue, useLoadAllGoogleFontVariants } from '../../../../lib/google-fonts'
import { useUnhandledError } from '../../../rapid-ui'
import { fontLoadingCountAtom } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { FontFamilyMode, FontFamilyProvider } from '../data/v1'
import { resolveFontFamilyCSS } from './resolve-font-family'

export type FontFamilyStyleResult = {
	style: Pick<CSSProperties, 'fontFamily' | 'visibility'>
	isLoading: boolean
}

/**
 * Resolves font family fields into a CSS fontFamily property.
 * When the font is a Google Font, triggers loading ALL variants via
 * the FontFace API so any weight/style used by child layers works.
 * When the font is a custom font, loads it from the stored URL.
 *
 * Returns `isLoading: true` while the font file is being fetched.
 * While loading, text is hidden via `visibility: hidden` to prevent FOUT.
 */
export function useFontFamilyStyle(
	mode: FontFamilyMode,
	provider: FontFamilyProvider,
	googleFamily: string,
	customUrl?: string,
	customName?: string,
): FontFamilyStyleResult {
	const isGoogleFont = mode === 'value' && provider === 'google-fonts' && googleFamily !== ''
	const isCustomFont = mode === 'value' && provider === 'custom' && !!customUrl && !!customName
	const { data: catalogue } = useGoogleFontCatalogue(isGoogleFont)
	const meta = isGoogleFont
		? catalogue?.find((f) => f.family === googleFamily)
		: undefined

	const { isLoading: googleLoading, error } = useLoadAllGoogleFontVariants({
		family: isGoogleFont ? googleFamily : '',
		meta,
	})

	const { triggerError } = useUnhandledError()
	useEffect(() => {
		if (error && googleFamily) {
			triggerError(`Failed to load font "${googleFamily}"`, 'The ad may render with a fallback font.', {
				cause: error,
			})
		}
	}, [error, googleFamily, triggerError])

	// Load custom font from stored URL
	const [customLoading, setCustomLoading] = useState(false)
	useEffect(() => {
		if (!isCustomFont) {
			setCustomLoading(false)
			return
		}
		if (isFontLoaded(customName, '1 900', 'normal')) { return }
		setCustomLoading(true)
		loadFont(customName, customUrl, '1 900', 'normal')
			.then(() => setCustomLoading(false))
			.catch((loadError) => {
				setCustomLoading(false)
				triggerError(`Failed to load custom font "${customName}"`, 'The ad may render with a fallback font.', {
					cause: loadError,
				})
			})
	}, [isCustomFont, customName, customUrl, triggerError])

	const isLoading = mode === 'value' && ((isGoogleFont && googleLoading) || (isCustomFont && customLoading))

	// Track loading count at the canvas level so the export/UI can react
	const store = useCanvasStore()
	const wasLoadingRef = useRef(false)
	useEffect(() => {
		if (isLoading && !wasLoadingRef.current) {
			store.set(fontLoadingCountAtom, (c) => c + 1)
		} else if (!isLoading && wasLoadingRef.current) {
			store.set(fontLoadingCountAtom, (c) => Math.max(0, c - 1))
		}
		wasLoadingRef.current = isLoading
	}, [isLoading, store])

	// Decrement on unmount if still loading
	useEffect(() => {
		return () => {
			if (wasLoadingRef.current) {
				store.set(fontLoadingCountAtom, (c) => Math.max(0, c - 1))
			}
		}
	}, [store])

	if (mode !== 'value') { return { style: {}, isLoading: false } }

	const fontFamily = resolveFontFamilyCSS(provider, googleFamily, customName)
	const fontStyle: Pick<CSSProperties, 'fontFamily'> = fontFamily ? { fontFamily } : {}

	if (isLoading) {
		return { style: { ...fontStyle, visibility: 'hidden' }, isLoading: true }
	}

	return { style: fontStyle, isLoading: false }
}
